import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userService } from '../services/api';
import { User } from '../types';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, Button, Typography, Chip, FormControl, InputLabel,
  Select, MenuItem, Stack, IconButton, Tooltip, Container, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider, Alert, Snackbar,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LogoutIcon from '@mui/icons-material/Logout';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import InputAdornment from '@mui/material/InputAdornment';

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

const getRoleColor = (roleId: number): ChipColor => {
  const colors: Record<number, ChipColor> = { 1: 'primary', 2: 'secondary' };
  return colors[roleId] ?? 'default';
};

const AdminPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Dialog open states
  const [viewOpen, setViewOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [resetPwOpen, setResetPwOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Form states
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', role_id: 2 });
  const [editForm, setEditForm] = useState({ name: '', email: '', role_id: 2 });
  const [resetPwForm, setResetPwForm] = useState({ new_password: '', confirm_password: '' });
  const [formError, setFormError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Snackbar
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const [response, totalResponse] = await Promise.all([
        userService.getAll({ params: { limit: rowsPerPage, offset: page * rowsPerPage, search: searchTerm, role_id: roleFilter, is_active: statusFilter } }),
        userService.getAll(),
      ]);
      setUsers(response.data);
      setTotal(totalResponse.data.length);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, [page, rowsPerPage, searchTerm, roleFilter, statusFilter]); // eslint-disable-line

  // Create
  const handleCreate = async () => {
    setFormError('');
    if (!createForm.name || !createForm.email || !createForm.password) {
      setFormError('All fields are required');
      return;
    }
    if (createForm.password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }
    try {
      await userService.create({ ...createForm });
      setCreateOpen(false);
      setCreateForm({ name: '', email: '', password: '', role_id: 2 });
      fetchUsers();
      showSnack('User created successfully');
    } catch (error: any) {
      setFormError(error.message || 'Failed to create user');
    }
  };

  // Edit
  const openEdit = (u: User) => {
    setSelectedUser(u);
    setEditForm({ name: u.name, email: u.email, role_id: u.role_id });
    setFormError('');
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!selectedUser) return;
    setFormError('');
    if (!editForm.name || !editForm.email) {
      setFormError('Name and email are required');
      return;
    }
    try {
      await userService.updateUser(selectedUser.id, editForm);
      setEditOpen(false);
      setSelectedUser(null);
      fetchUsers();
      showSnack('User updated successfully');
    } catch (error: any) {
      setFormError(error.message || 'Failed to update user');
    }
  };

  // Reset Password
  const handleResetPassword = async () => {
    if (!selectedUser) return;
    setFormError('');
    if (!resetPwForm.new_password) {
      setFormError('Password is required');
      return;
    }
    if (resetPwForm.new_password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }
    if (resetPwForm.new_password !== resetPwForm.confirm_password) {
      setFormError('Passwords do not match');
      return;
    }
    try {
      await userService.resetPassword(selectedUser.id, resetPwForm.new_password);
      setResetPwOpen(false);
      setSelectedUser(null);
      setResetPwForm({ new_password: '', confirm_password: '' });
      showSnack('Password reset successfully');
    } catch (error: any) {
      setFormError(error.message || 'Failed to reset password');
    }
  };

  // Toggle Active / Inactive (archive)
  const handleToggleStatus = async () => {
    if (!selectedUser) return;
    try {
      await userService.updateStatus(selectedUser.id, !selectedUser.is_active);
      setStatusOpen(false);
      showSnack(`User ${!selectedUser.is_active ? 'activated' : 'deactivated'} successfully`);
      setSelectedUser(null);
      fetchUsers();
    } catch (error: any) {
      showSnack(error.message || 'Failed to update status', 'error');
    }
  };

  // Delete
  const handleConfirmDelete = async () => {
    if (!selectedUser) return;
    try {
      await userService.delete(selectedUser.id);
      setDeleteOpen(false);
      setSelectedUser(null);
      fetchUsers();
      showSnack('User deleted successfully');
    } catch (error: any) {
      showSnack(error.message || 'Failed to delete user', 'error');
    }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
          <div>
            <Typography variant="h4" gutterBottom>User Administration</Typography>
            <Typography variant="body1" color="text.secondary">Welcome, {user?.name}</Typography>
          </div>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => { setCreateForm({ name: '', email: '', password: '', role_id: 2 }); setFormError(''); setShowPassword(false); setCreateOpen(true); }}>
              Create User
            </Button>
            <Button variant="outlined" startIcon={<LogoutIcon />} onClick={handleLogout} color="error">
              Logout
            </Button>
          </Stack>
        </Stack>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField placeholder="Search users..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
              sx={{ flex: 1 }} size="small" />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Role</InputLabel>
              <Select value={roleFilter} label="Role" onChange={(e: SelectChangeEvent) => setRoleFilter(e.target.value)}>
                <MenuItem value="">All Roles</MenuItem>
                <MenuItem value="1">Admin</MenuItem>
                <MenuItem value="2">User</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
            {(searchTerm || roleFilter || statusFilter) && (
              <Button onClick={() => { setSearchTerm(''); setRoleFilter(''); setStatusFilter(''); }} variant="text">Clear</Button>
            )}
          </Stack>
        </Paper>

        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}>Loading...</TableCell></TableRow>
              ) : users.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}>No users found</TableCell></TableRow>
              ) : users.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell><Typography variant="body1" fontWeight="medium">{u.name}</Typography></TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Chip label={u.role_id === 1 ? 'Admin' : 'User'} size="small" color={getRoleColor(u.role_id)} />
                  </TableCell>
                  <TableCell>
                    <Chip label={u.is_active ? 'Active' : 'Inactive'} size="small" color={u.is_active ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => { setSelectedUser(u); setViewOpen(true); }}>
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit User">
                      <IconButton size="small" onClick={() => openEdit(u)} color="primary">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset Password">
                      <IconButton size="small" color="warning"
                        onClick={() => { setSelectedUser(u); setResetPwForm({ new_password: '', confirm_password: '' }); setFormError(''); setResetPwOpen(true); }}>
                        <LockResetIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={u.is_active ? 'Deactivate (Archive)' : 'Activate'}>
                      <IconButton size="small" color={u.is_active ? 'default' : 'success'}
                        onClick={() => { setSelectedUser(u); setStatusOpen(true); }}>
                        {u.is_active ? <PersonOffIcon /> : <PersonIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete User">
                      <IconButton size="small" color="error"
                        onClick={() => { setSelectedUser(u); setDeleteOpen(true); }}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={total}
            rowsPerPage={rowsPerPage} page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
        </TableContainer>

        {/* View Dialog */}
        <Dialog open={viewOpen} onClose={() => { setViewOpen(false); setSelectedUser(null); }} maxWidth="sm" fullWidth>
          <DialogTitle>User Details</DialogTitle>
          <Divider />
          <DialogContent>
            {selectedUser && (
              <Box mt={2}>
                <Typography variant="h6" gutterBottom>{selectedUser.name}</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>Email: {selectedUser.email}</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>Role: {selectedUser.role_id === 1 ? 'Admin' : 'User'}</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>Status: {selectedUser.is_active ? 'Active' : 'Inactive'}</Typography>
                {selectedUser.created_at && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Created: {new Date(selectedUser.created_at).toLocaleDateString()}
                  </Typography>
                )}
                {selectedUser.last_login && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Last Login: {new Date(selectedUser.last_login).toLocaleString()}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setViewOpen(false); setSelectedUser(null); }}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Create User Dialog */}
        <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create New User</DialogTitle>
          <Divider />
          <DialogContent>
            <input type="text" style={{ display: 'none' }} autoComplete="off" />
            <input type="password" style={{ display: 'none' }} autoComplete="new-password" />
            <Stack spacing={2} mt={1}>
              {formError && <Alert severity="error">{formError}</Alert>}
              <TextField label="Name" value={createForm.name}
                onChange={(e) => setCreateForm(f => ({ ...f, name: e.target.value }))}
                fullWidth required autoComplete="off" />
              <TextField label="Email" type="email" value={createForm.email}
                onChange={(e) => setCreateForm(f => ({ ...f, email: e.target.value }))}
                fullWidth required autoComplete="off" />
              <TextField label="Password" type={showPassword ? 'text' : 'password'} value={createForm.password}
                onChange={(e) => setCreateForm(f => ({ ...f, password: e.target.value }))}
                fullWidth required helperText="Minimum 8 characters" autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword(v => !v)} edge="end">
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }} />
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select value={createForm.role_id} label="Role"
                  onChange={(e) => setCreateForm(f => ({ ...f, role_id: Number(e.target.value) }))}>
                  <MenuItem value={1}>Admin</MenuItem>
                  <MenuItem value={2}>User</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} variant="contained">Create</Button>
          </DialogActions>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editOpen} onClose={() => { setEditOpen(false); setSelectedUser(null); }} maxWidth="sm" fullWidth>
          <DialogTitle>Edit User</DialogTitle>
          <Divider />
          <DialogContent>
            <Stack spacing={2} mt={1}>
              {formError && <Alert severity="error">{formError}</Alert>}
              <TextField label="Name" value={editForm.name}
                onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))} fullWidth required />
              <TextField label="Email" type="email" value={editForm.email}
                onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))} fullWidth required />
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select value={editForm.role_id} label="Role"
                  onChange={(e) => setEditForm(f => ({ ...f, role_id: Number(e.target.value) }))}>
                  <MenuItem value={1}>Admin</MenuItem>
                  <MenuItem value={2}>User</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setEditOpen(false); setSelectedUser(null); }}>Cancel</Button>
            <Button onClick={handleEdit} variant="contained">Save</Button>
          </DialogActions>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={resetPwOpen} onClose={() => { setResetPwOpen(false); setSelectedUser(null); }} maxWidth="sm" fullWidth>
          <DialogTitle>Reset Password — {selectedUser?.name}</DialogTitle>
          <Divider />
          <DialogContent>
            <Stack spacing={2} mt={1}>
              {formError && <Alert severity="error">{formError}</Alert>}
              <TextField label="New Password" name="new_password" type="password" value={resetPwForm.new_password}
                onChange={(e) => setResetPwForm(f => ({ ...f, new_password: e.target.value }))}
                fullWidth required helperText="Minimum 8 characters" autoComplete="new-password" />
              <TextField label="Confirm Password" name="confirm_password" type="password" value={resetPwForm.confirm_password}
                onChange={(e) => setResetPwForm(f => ({ ...f, confirm_password: e.target.value }))}
                fullWidth required autoComplete="new-password" />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setResetPwOpen(false); setSelectedUser(null); }}>Cancel</Button>
            <Button onClick={handleResetPassword} variant="contained" color="warning">Reset Password</Button>
          </DialogActions>
        </Dialog>

        {/* Toggle Status Dialog */}
        <Dialog open={statusOpen} onClose={() => { setStatusOpen(false); setSelectedUser(null); }} maxWidth="sm">
          <DialogTitle>{selectedUser?.is_active ? 'Deactivate User' : 'Activate User'}</DialogTitle>
          <Divider />
          <DialogContent>
            <Typography>
              {selectedUser?.is_active
                ? `Deactivate ${selectedUser?.name}? They will no longer be able to login.`
                : `Activate ${selectedUser?.name}? They will be able to login again.`}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setStatusOpen(false); setSelectedUser(null); }}>Cancel</Button>
            <Button onClick={handleToggleStatus} variant="contained"
              color={selectedUser?.is_active ? 'error' : 'success'}>
              {selectedUser?.is_active ? 'Deactivate' : 'Activate'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteOpen} onClose={() => { setDeleteOpen(false); setSelectedUser(null); }} maxWidth="sm">
          <DialogTitle>Delete User</DialogTitle>
          <Divider />
          <DialogContent>
            <Typography>Permanently delete <strong>{selectedUser?.name}</strong>? This cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setDeleteOpen(false); setSelectedUser(null); }}>Cancel</Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">Delete</Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snack.open} autoHideDuration={4000}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
            {snack.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default AdminPage;
